import React, { useState, useEffect, useRef } from 'react';
import { OptionFlowAlert, OptionFlowResponse } from '../types';
import { apiService } from '../services/apiService';
import { NewsTicker } from './NewsTicker';
import { OptionFlowVisualization } from './OptionFlowVisualization';

type FilterType = 'all' | 'high-premium' | 'golden-flows' | 'call-sell' | 'call-buy' | 'put-sell' | 'put-buy';
type SortField = 'ticker' | 'type' | 'strike' | 'expiry' | 'total_premium' | 'volume' | 'created_at' | 'underlying_price' | 'price' | 'bid' | 'ask' | 'open_interest' | 'trade_count' | 'total_size' | 'total_bid_side_prem' | 'total_ask_side_prem' | 'marketcap' | 'volume_oi_ratio';
type SortDirection = 'asc' | 'desc';

export const OptionFlowFeed: React.FC = () => {
  const [alerts, setAlerts] = useState<OptionFlowAlert[]>([]);
  const [filter, setFilter] = useState<FilterType>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [premiumMin, setPremiumMin] = useState<string>('');
  const [premiumMax, setPremiumMax] = useState<string>('');
  const [showPremiumFilter, setShowPremiumFilter] = useState<boolean>(false);
  const [showVOIFilter, setShowVOIFilter] = useState<boolean>(false);
  const [filterDropdownPosition, setFilterDropdownPosition] = useState<{top: number, left: number}>({top: 0, left: 0});
  const [voiDropdownPosition, setVOIDropdownPosition] = useState<{top: number, left: number}>({top: 0, left: 0});
  const [voiMin, setVOIMin] = useState<string>('');
  const [voiMax, setVOIMax] = useState<string>('');
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const voiFilterButtonRef = useRef<HTMLButtonElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const voiFilterDropdownRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(100);
  const [cacheStats, setCacheStats] = useState<any>(null);
  const [showCacheControls, setShowCacheControls] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Date filter state
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [isDateManuallySet, setIsDateManuallySet] = useState<boolean>(false);
  
  // Use refs to avoid stale closure in auto-refresh interval
  const selectedDateRef = useRef<string>('');
  const isDateManuallySetRef = useRef<boolean>(false);
  
  // Keep refs in sync with state
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);
  
  useEffect(() => {
    isDateManuallySetRef.current = isDateManuallySet;
  }, [isDateManuallySet]);

  useEffect(() => {
    loadOptionFlow();
    updateCacheStats();
  }, []);

  useEffect(() => {
    // Auto-refresh every 30 seconds
    const interval = setInterval(async () => {
      console.log('Auto-refresh triggered at:', new Date().toLocaleTimeString());
      setIsRefreshing(true);
      await loadOptionFlow(false, false);
      await updateCacheStats();
      setIsRefreshing(false);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    resetPagination();
  }, [filter, searchTerm, sortField, sortDirection, premiumMin, premiumMax, voiMin, voiMax, selectedDate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showPremiumFilter || showVOIFilter) {
        const target = event.target as Node;
        const isClickOnPremiumButton = filterButtonRef.current && filterButtonRef.current.contains(target);
        const isClickOnVOIButton = voiFilterButtonRef.current && voiFilterButtonRef.current.contains(target);
        const isClickOnPremiumDropdown = filterDropdownRef.current && filterDropdownRef.current.contains(target);
        const isClickOnVOIDropdown = voiFilterDropdownRef.current && voiFilterDropdownRef.current.contains(target);
        
        if (showPremiumFilter && !isClickOnPremiumButton && !isClickOnPremiumDropdown) {
          setShowPremiumFilter(false);
        }
        
        if (showVOIFilter && !isClickOnVOIButton && !isClickOnVOIDropdown) {
          setShowVOIFilter(false);
        }
      }
    };

    if (showPremiumFilter || showVOIFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPremiumFilter, showVOIFilter]);

  const loadOptionFlow = async (forceRefresh: boolean = false, showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      const data: OptionFlowResponse = await apiService.getOptionFlow({ limit: 200 }, !forceRefresh);
      
      // Check for duplicates and deduplicate
      const uniqueIds = new Set<string>();
      const duplicates: string[] = [];
      data.data.forEach(alert => {
        if (uniqueIds.has(alert.id)) {
          duplicates.push(alert.id);
        }
        uniqueIds.add(alert.id);
      });
      
      let loadedAlerts: OptionFlowAlert[];
      
      if (duplicates.length > 0) {
        const deduped = Array.from(
          data.data.reduce((map, alert) => {
            if (!map.has(alert.id) || new Date(alert.created_at) > new Date(map.get(alert.id)!.created_at)) {
              map.set(alert.id, alert);
            }
            return map;
          }, new Map<string, OptionFlowAlert>()).values()
        );
        loadedAlerts = deduped;
        setAlerts(deduped);
      } else {
        loadedAlerts = data.data;
        setAlerts(data.data);
      }
      
      // Set default date to the latest date from the data
      if (loadedAlerts.length > 0 && !selectedDateRef.current && !isDateManuallySetRef.current) {
        const dates = loadedAlerts.map(alert => new Date(alert.created_at).toISOString().split('T')[0]);
        const latestDate = dates.sort().reverse()[0];
        setSelectedDate(latestDate);
      }
      
      setLastRefreshTime(new Date());
    } catch (err) {
      console.error('Failed to load option flow data:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const updateCacheStats = async () => {
    try {
      const stats = await apiService.getCacheStats();
      setCacheStats(stats);
    } catch (error) {
      console.error('Failed to get cache stats:', error);
    }
  };

  const filterAlerts = (alerts: OptionFlowAlert[], filterType: FilterType): OptionFlowAlert[] => {
    switch (filterType) {
      case 'all':
        return alerts;
      case 'high-premium':
        return alerts.filter(alert => {
          const premium = typeof alert.total_premium === 'string' ? parseFloat(alert.total_premium) : alert.total_premium;
          return premium >= 100000;
        });
      case 'golden-flows':
        return alerts.filter(alert => {
          const premium = typeof alert.total_premium === 'string' ? parseFloat(alert.total_premium) : alert.total_premium;
          return premium >= 1000000;
        });
      case 'call-sell':
        return alerts.filter(alert => {
          if (alert.type !== 'call') return false;
          const bidPrem = typeof alert.total_bid_side_prem === 'string' ? parseFloat(alert.total_bid_side_prem) : alert.total_bid_side_prem;
          const askPrem = typeof alert.total_ask_side_prem === 'string' ? parseFloat(alert.total_ask_side_prem) : alert.total_ask_side_prem;
          return bidPrem > askPrem;
        });
      case 'call-buy':
        return alerts.filter(alert => {
          if (alert.type !== 'call') return false;
          const bidPrem = typeof alert.total_bid_side_prem === 'string' ? parseFloat(alert.total_bid_side_prem) : alert.total_bid_side_prem;
          const askPrem = typeof alert.total_ask_side_prem === 'string' ? parseFloat(alert.total_ask_side_prem) : alert.total_ask_side_prem;
          return askPrem > bidPrem;
        });
      case 'put-sell':
        return alerts.filter(alert => {
          if (alert.type !== 'put') return false;
          const bidPrem = typeof alert.total_bid_side_prem === 'string' ? parseFloat(alert.total_bid_side_prem) : alert.total_bid_side_prem;
          const askPrem = typeof alert.total_ask_side_prem === 'string' ? parseFloat(alert.total_ask_side_prem) : alert.total_ask_side_prem;
          return bidPrem > askPrem;
        });
      case 'put-buy':
        return alerts.filter(alert => {
          if (alert.type !== 'put') return false;
          const bidPrem = typeof alert.total_bid_side_prem === 'string' ? parseFloat(alert.total_bid_side_prem) : alert.total_bid_side_prem;
          const askPrem = typeof alert.total_ask_side_prem === 'string' ? parseFloat(alert.total_ask_side_prem) : alert.total_ask_side_prem;
          return askPrem > bidPrem;
        });
      default:
        return alerts;
    }
  };

  const formatPremium = (premium: number | string) => {
    const numValue = typeof premium === 'string' ? parseFloat(premium) : premium;
    return `$${numValue.toLocaleString()}`;
  };

  const getTypeColor = (type: string) => {
    return type === 'call' ? 'call' : 'put';
  };

  const getTypeIcon = (type: string) => {
    return type === 'call' ? '📈' : '📉';
  };

  const formatESTTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatMarketCap = (marketcap: number | null) => {
    if (!marketcap) return 'N/A';
    if (marketcap >= 1000000000) {
      return `$${(marketcap / 1000000000).toFixed(1)}B`;
    } else if (marketcap >= 1000000) {
      return `$${(marketcap / 1000000).toFixed(1)}M`;
    } else if (marketcap >= 1000) {
      return `$${(marketcap / 1000).toFixed(0)}K`;
    }
    return `$${marketcap}`;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const searchAlerts = (alerts: OptionFlowAlert[], searchTerm: string): OptionFlowAlert[] => {
    let filteredAlerts = alerts;
    
    // Apply date filter first
    if (selectedDate) {
      filteredAlerts = filteredAlerts.filter(alert => {
        const alertDate = new Date(alert.created_at);
        const alertDateString = alertDate.toISOString().split('T')[0];
        return alertDateString === selectedDate;
      });
    }
    
    // Apply text search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredAlerts = filteredAlerts.filter(alert => {
        const searchableFields = [
          alert.ticker, alert.type, alert.strike?.toString(), alert.expiry,
          alert.underlying_price?.toString(), alert.price?.toString(),
          alert.total_premium?.toString(), alert.volume?.toString(),
          alert.sector, alert.option_chain, alert.alert_rule
        ].filter(Boolean);
        
        return searchableFields.some(field => 
          field?.toLowerCase().includes(searchLower)
        );
      });
    }
    
    // Apply premium range filtering
    if (premiumMin.trim() || premiumMax.trim()) {
      filteredAlerts = filteredAlerts.filter(alert => {
        const totalPremium = typeof alert.total_premium === 'string' 
          ? parseFloat(alert.total_premium) 
          : alert.total_premium;
        
        if (isNaN(totalPremium)) return false;
        
        const minValue = premiumMin.trim() ? parseFloat(premiumMin) : 0;
        const maxValue = premiumMax.trim() ? parseFloat(premiumMax) : Infinity;
        
        return totalPremium >= minValue && totalPremium <= maxValue;
      });
    }
    
    // Apply V/OI range filtering
    if (voiMin.trim() || voiMax.trim()) {
      filteredAlerts = filteredAlerts.filter(alert => {
        const voiRatio = typeof alert.volume_oi_ratio === 'string' 
          ? parseFloat(alert.volume_oi_ratio) 
          : alert.volume_oi_ratio;
        
        if (isNaN(voiRatio)) return false;
        
        const minValue = voiMin.trim() ? parseFloat(voiMin) : 0;
        const maxValue = voiMax.trim() ? parseFloat(voiMax) : Infinity;
        
        return voiRatio >= minValue && voiRatio <= maxValue;
      });
    }
    
    return filteredAlerts;
  };

  const filteredAlerts = searchAlerts(filterAlerts(alerts, filter), searchTerm);
  
  const sortedAlerts = [...filteredAlerts].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    if (sortField === 'created_at') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    } else if (['total_premium', 'total_ask_side_prem', 'total_bid_side_prem', 'underlying_price', 'price', 'bid', 'ask', 'volume', 'open_interest', 'trade_count', 'total_size', 'marketcap', 'volume_oi_ratio'].includes(sortField)) {
      aValue = typeof aValue === 'string' ? parseFloat(aValue) || 0 : aValue;
      bValue = typeof bValue === 'string' ? parseFloat(bValue) || 0 : bValue;
    } else if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAlerts = sortedAlerts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetPagination = () => {
    setCurrentPage(1);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setIsDateManuallySet(true);
  };

  return (
    <div className="component-container">
      {/* News Ticker */}
      <NewsTicker />
      
      {/* Search and Filter Controls */}
      <div className="search-filter-container">
        <div className="search-container">
          <div className="search-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search ticker, strike, expiry, premium, volume, etc..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
            className="search-input"
            style={{ textTransform: 'uppercase' }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="search-clear">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="filter-controls-row">
          <div className="filter-buttons">
            {[
              { key: 'all', label: 'All Trades' },
              { key: 'high-premium', label: 'High Premium' },
              { key: 'golden-flows', label: 'Golden' },
              { key: 'call-buy', label: 'Call Buy' },
              { key: 'put-sell', label: 'Put Sell' },
              { key: 'call-sell', label: 'Call Sell' },
              { key: 'put-buy', label: 'Put Buy' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key as FilterType)}
                className={`filter-button ${filter === key ? 'filter-button-active' : 'filter-button-inactive'}`}
              >
                {label}
              </button>
            ))}
          </div>
          
          <div className="controls-group">
            <div className="cache-controls">
              <button
                onClick={() => setShowCacheControls(!showCacheControls)}
                className="cache-toggle-button"
                title="Toggle cache controls"
              >
                📊 Cache ({cacheStats?.total_alerts || 0})
              </button>
              {showCacheControls && (
                <div className="cache-controls-panel">
                  <div className="cache-stats">
                    <div className="cache-stat">
                      <span className="cache-stat-label">Total Alerts:</span>
                      <span className="cache-stat-value">{cacheStats?.total_alerts || 0}</span>
                    </div>
                    <div className="cache-stat">
                      <span className="cache-stat-label">Cache Entries:</span>
                      <span className="cache-stat-value">{cacheStats?.total_entries || 0}</span>
                    </div>
                  </div>
                  <div className="cache-actions">
                    <button onClick={() => loadOptionFlow(true)} className="cache-action-button" title="Force refresh data">
                      Force Refresh
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="refresh-info">
              {lastRefreshTime && (
                <span className="last-refresh">
                  Last updated: {lastRefreshTime.toLocaleTimeString()}
                  {isRefreshing && (
                    <span className="refreshing-indicator">
                      <span className="refreshing-dot"></span>
                      Refreshing...
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        {searchTerm && (
          <div className="search-results-count">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredAlerts.length)} of {filteredAlerts.length} results for "{searchTerm}"
          </div>
        )}
      </div>

      {/* Option Flow Visualization */}
      {alerts.length > 0 && (
        <OptionFlowVisualization 
          alerts={filteredAlerts}
          ticker={searchTerm.trim() || undefined}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          availableDates={Array.from(new Set(alerts.map(alert => new Date(alert.created_at).toISOString().split('T')[0])))}
        />
      )}

      {/* Top Pagination Controls */}
      {filteredAlerts.length > itemsPerPage && (
        <div className="pagination-container pagination-top">
          <div className="pagination-info">
            <span>Showing {startIndex + 1} to {Math.min(endIndex, filteredAlerts.length)} of {filteredAlerts.length} results</span>
          </div>
          <div className="pagination-controls">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-button">Previous</button>
            <div className="pagination-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                  <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`pagination-number ${currentPage === pageNum ? 'pagination-number-active' : 'pagination-number-inactive'}`}>
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-button">Next</button>
          </div>
        </div>
      )}

      {/* Option Flow Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading option flow data...</p>
          </div>
        ) : (
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell" onClick={() => handleSort('ticker')}>
                  <div className="table-header-content">Ticker {getSortIcon('ticker')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('type')}>
                  <div className="table-header-content">Type {getSortIcon('type')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('strike')}>
                  <div className="table-header-content">Strike {getSortIcon('strike')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('expiry')}>
                  <div className="table-header-content">Expiry {getSortIcon('expiry')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('total_premium')}>
                  <div className="table-header-content">
                    Premium {getSortIcon('total_premium')}
                    <button
                      ref={filterButtonRef}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (filterButtonRef.current) {
                          const rect = filterButtonRef.current.getBoundingClientRect();
                          setFilterDropdownPosition({ top: rect.bottom + 4, left: Math.max(10, rect.left) });
                        }
                        setShowPremiumFilter(!showPremiumFilter);
                      }}
                      className="column-filter-button"
                      title="Filter by premium range"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </button>
                  </div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('total_ask_side_prem')}>
                  <div className="table-header-content">Buy Premium {getSortIcon('total_ask_side_prem')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('total_bid_side_prem')}>
                  <div className="table-header-content">Sell Premium {getSortIcon('total_bid_side_prem')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('created_at')}>
                  <div className="table-header-content">Time (EST) {getSortIcon('created_at')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('underlying_price')}>
                  <div className="table-header-content">Underlying {getSortIcon('underlying_price')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('total_size')}>
                  <div className="table-header-content">Size {getSortIcon('total_size')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('price')}>
                  <div className="table-header-content">Price {getSortIcon('price')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('volume')}>
                  <div className="table-header-content">Volume {getSortIcon('volume')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('open_interest')}>
                  <div className="table-header-content">OI {getSortIcon('open_interest')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('trade_count')}>
                  <div className="table-header-content">Trades {getSortIcon('trade_count')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('marketcap')}>
                  <div className="table-header-content">Market Cap {getSortIcon('marketcap')}</div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('volume_oi_ratio')}>
                  <div className="table-header-content">
                    V/OI {getSortIcon('volume_oi_ratio')}
                    <button
                      ref={voiFilterButtonRef}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (voiFilterButtonRef.current) {
                          const rect = voiFilterButtonRef.current.getBoundingClientRect();
                          setVOIDropdownPosition({ top: rect.bottom + 4, left: Math.max(10, rect.left) });
                        }
                        setShowVOIFilter(!showVOIFilter);
                      }}
                      className="column-filter-button"
                      title="Filter by V/OI ratio"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            
            {/* Premium Filter Dropdown */}
            {showPremiumFilter && (
              <div ref={filterDropdownRef} className="column-filter-dropdown" style={{ top: `${filterDropdownPosition.top}px`, left: `${filterDropdownPosition.left}px` }}>
                <div className="column-filter-content">
                  <div className="column-filter-header">
                    <h4>Filter by Premium</h4>
                    <button onClick={() => setShowPremiumFilter(false)} className="column-filter-close">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="column-filter-inputs">
                    <div className="column-filter-input-group">
                      <label>Minimum Premium</label>
                      <input type="number" placeholder="Min ($)" value={premiumMin} onChange={(e) => setPremiumMin(e.target.value)} className="column-filter-input" />
                    </div>
                    <div className="column-filter-input-group">
                      <label>Maximum Premium</label>
                      <input type="number" placeholder="Max ($)" value={premiumMax} onChange={(e) => setPremiumMax(e.target.value)} className="column-filter-input" />
                    </div>
                  </div>
                  <div className="column-filter-actions">
                    <button onClick={() => { setPremiumMin(''); setPremiumMax(''); }} className="column-filter-clear">Clear</button>
                    <button onClick={() => setShowPremiumFilter(false)} className="column-filter-apply">Apply Filter</button>
                  </div>
                </div>
              </div>
            )}
            
            {/* V/OI Filter Dropdown */}
            {showVOIFilter && (
              <div ref={voiFilterDropdownRef} className="column-filter-dropdown" style={{ top: `${voiDropdownPosition.top}px`, left: `${voiDropdownPosition.left}px` }}>
                <div className="column-filter-content">
                  <div className="column-filter-header">
                    <h4>Filter by V/OI Ratio</h4>
                    <button onClick={() => setShowVOIFilter(false)} className="column-filter-close">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="column-filter-inputs">
                    <div className="column-filter-input-group">
                      <label>Minimum V/OI</label>
                      <input type="number" placeholder="Min" value={voiMin} onChange={(e) => setVOIMin(e.target.value)} className="column-filter-input" />
                    </div>
                    <div className="column-filter-input-group">
                      <label>Maximum V/OI</label>
                      <input type="number" placeholder="Max" value={voiMax} onChange={(e) => setVOIMax(e.target.value)} className="column-filter-input" />
                    </div>
                  </div>
                  <div className="column-filter-actions">
                    <button onClick={() => { setVOIMin(''); setVOIMax(''); }} className="column-filter-clear">Clear</button>
                    <button onClick={() => setShowVOIFilter(false)} className="column-filter-apply">Apply Filter</button>
                  </div>
                </div>
              </div>
            )}
            
            <tbody className="table-body">
              {paginatedAlerts.map((alert) => (
                <tr key={alert.id} className="table-row">
                  <td className="table-cell table-cell-primary">{alert.ticker}</td>
                  <td className="table-cell">
                    <span className={`type-badge ${getTypeColor(alert.type)}`}>
                      {getTypeIcon(alert.type)} {alert.type.toUpperCase()}
                    </span>
                  </td>
                  <td className="table-cell table-cell-secondary">${alert.strike}</td>
                  <td className="table-cell table-cell-secondary">{alert.expiry}</td>
                  <td className="table-cell table-cell-primary">{formatPremium(alert.total_premium)}</td>
                  <td className="table-cell table-cell-secondary">{formatPremium(alert.total_ask_side_prem)}</td>
                  <td className="table-cell table-cell-secondary">{formatPremium(alert.total_bid_side_prem)}</td>
                  <td className="table-cell table-cell-secondary">{formatESTTime(alert.created_at)}</td>
                  <td className="table-cell table-cell-secondary">${typeof alert.underlying_price === 'number' ? alert.underlying_price.toFixed(2) : alert.underlying_price || 'N/A'}</td>
                  <td className="table-cell table-cell-secondary">{alert.total_size?.toLocaleString() || 'N/A'}</td>
                  <td className="table-cell table-cell-secondary">${typeof alert.price === 'number' ? alert.price.toFixed(2) : alert.price || 'N/A'}</td>
                  <td className="table-cell table-cell-secondary">{alert.volume?.toLocaleString() || 'N/A'}</td>
                  <td className="table-cell table-cell-secondary">{alert.open_interest?.toLocaleString() || 'N/A'}</td>
                  <td className="table-cell table-cell-secondary">{alert.trade_count || 'N/A'}</td>
                  <td className="table-cell table-cell-secondary">{formatMarketCap(alert.marketcap)}</td>
                  <td className="table-cell table-cell-secondary">{alert.volume_oi_ratio ? (typeof alert.volume_oi_ratio === 'number' ? alert.volume_oi_ratio.toFixed(2) : alert.volume_oi_ratio) : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {alerts.length === 0 && !loading && (
        <div className="empty-state">No option flow data available</div>
      )}

      {/* Bottom Pagination Controls */}
      {filteredAlerts.length > itemsPerPage && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>Showing {startIndex + 1} to {Math.min(endIndex, filteredAlerts.length)} of {filteredAlerts.length} results</span>
          </div>
          <div className="pagination-controls">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-button">Previous</button>
            <div className="pagination-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                  <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`pagination-number ${currentPage === pageNum ? 'pagination-number-active' : 'pagination-number-inactive'}`}>
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-button">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};
